import { Box, Button, Typography } from "@mui/material"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query"
import { TelegramMessageEditor } from "@core/components/telegram/editor/telegramMessageEditor"
import { TelegramMessagePreview } from "@core/components/telegram/preview/telegramMessagePreview"
import {
  fromVersionContent,
  type TemplateWriteAccess,
} from "../hooks/useVersionDraft"
import { useGetTemplateVersionQuery } from "../services/publications/templatesApi"
import { PostBotNavigation } from "../components/common/postBotNavigation"
import {
  PostBotEditorLayout,
  PostBotPageLayout,
} from "../components/common/postBotPageLayout"
import { TemplateQueryFeedback } from "../components/templates/templateQueryFeedback"
import { useMediaPreview } from "../hooks/useMediaPreview"
import { useMediaVisibility } from "../hooks/useMediaVisibility"
import {
  readTemplateReturnContext,
  newTemplateVersionPath,
  templatePath,
  templatesPath,
} from "../components/templates/templateNavigation"

export interface TemplateVersionPageProps extends TemplateWriteAccess {
  versionId?: string
}
/** Direct reload fetches the requested immutable version, not the newest one. */
export default function TemplateVersionPage(props: TemplateVersionPageProps) {
  const params = useParams()
  const versionId = props.versionId ?? params.versionId ?? params.id
  return (
    <VersionDetail
      key={versionId ?? "missing"}
      {...props}
      versionId={versionId}
    />
  )
}
function VersionDetail({ versionId, ...access }: TemplateVersionPageProps) {
  const { state } = useLocation()
  const navigate = useNavigate()
  const returnContext = readTemplateReturnContext(state)
  const query = useGetTemplateVersionQuery(versionId ?? skipToken)
  const version =
    query.currentData?.id === versionId ? query.currentData : undefined
  const content = version ? fromVersionContent(version.content) : undefined
  const viewport = useMediaVisibility()
  const media = useMediaPreview(content?.mediaIds ?? [], [], viewport.visible)
  return (
    <PostBotPageLayout>
      <PostBotNavigation />
      <Button
        component={Link}
        to={version ? templatePath(version.templateId) : templatesPath}
        state={returnContext}
        sx={{ alignSelf: "flex-start" }}
      >
        {version ? "Все версии шаблона" : "Библиотека шаблонов"}
      </Button>
      <TemplateQueryFeedback
        query={query}
        empty={!version && "Версия не найдена"}
      />
      {version && content && (
        <>
          <Typography variant="h5">Версия {version.number}</Typography>
          {!access.readOnly && access.canWrite !== false && (
            <Button
              component={Link}
              to={newTemplateVersionPath(version.templateId, version.id)}
              state={returnContext}
              sx={{ alignSelf: "flex-start" }}
            >
              Редактирование
            </Button>
          )}
          <PostBotEditorLayout
            preview={
              <Box ref={viewport.ref}>
                <TelegramMessagePreview value={content} media={media} />
              </Box>
            }
          >
            <TelegramMessageEditor
              value={content}
              media={media}
              onChange={() => {}}
              readOnly
              onBack={() =>
                navigate(templatePath(version.templateId), {
                  state: returnContext,
                })
              }
              onRecover={
                !access.readOnly && access.canWrite !== false
                  ? () =>
                      navigate(newTemplateVersionPath(version.templateId), {
                        state: returnContext,
                      })
                  : undefined
              }
            />
          </PostBotEditorLayout>
        </>
      )}
    </PostBotPageLayout>
  )
}
